/** @module @liquidefy/code-editor/diff/diff-review @version 2.0.0 @license LQD Research LLC */

// ═══════════════════════════════════════════════════════════════════════════════
// §1 IMPORTS
// ═══════════════════════════════════════════════════════════════════════════════

import type {
  ReviewState,
  ReviewThread,
  ReviewComment,
  ReviewThreadId,
  ReviewCommentId,
  DiffId,
  Range,
  EditorResult,
} from '../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  asReviewThreadId,
  asReviewCommentId,
  now,
  emptyReviewState,
} from '../code-editor.js';

// ═══════════════════════════════════════════════════════════════════════════════
// §2 LOCAL TYPES — ReviewSuggestion, ReviewApproval, ApprovalState
// ═══════════════════════════════════════════════════════════════════════════════

/** An inline code suggestion attached to a review thread. */
export interface ReviewSuggestion {
  readonly id: string;
  readonly threadId: ReviewThreadId;
  readonly range: Range;
  readonly replacement: string;
  readonly isApplied: boolean;
  readonly createdAt: number;
}

/** Approval state for a single reviewer. */
export type ApprovalState = 'approved' | 'changes-requested' | 'commented' | 'pending';

/** A single reviewer approval entry. */
export interface ReviewApproval {
  readonly userId: string;
  readonly state: ApprovalState;
  readonly submittedAt: number;
  readonly comment?: string;
}

/** Extended review state including suggestions and approvals. */
export interface FullReviewState {
  readonly diffId: DiffId;
  readonly base: ReviewState;
  readonly suggestions: ReviewSuggestion[];
  readonly approvals: ReviewApproval[];
}

/** Summary of review activity counts. */
export interface ReviewSummary {
  readonly threadCount: number;
  readonly openThreadCount: number;
  readonly resolvedThreadCount: number;
  readonly suggestionCount: number;
  readonly appliedSuggestionCount: number;
  readonly approvalCount: number;
  readonly changesRequestedCount: number;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §3 FACTORY — createReviewState
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Creates a new FullReviewState for a given diff.
 * @param diffId - The diff this review is associated with.
 * @returns Fresh FullReviewState in 'draft' status.
 */
export function createReviewState(diffId: DiffId): FullReviewState {
  return {
    diffId,
    base: emptyReviewState(),
    suggestions: [],
    approvals: [],
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §4 THREAD MANAGEMENT — addComment, replyToComment, resolveThread
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Adds a new review thread (top-level comment) to the review state.
 * @param state - Current review state.
 * @param thread - Thread to add (id will be overwritten with a generated one).
 * @returns EditorResult wrapping updated FullReviewState.
 */
export function addComment(
  state: FullReviewState,
  thread: Omit<ReviewThread, 'id' | 'comments' | 'status' | 'createdAt'>
    & { initialComment: Omit<ReviewComment, 'id' | 'threadId' | 'timestamp'> }
): EditorResult<FullReviewState> {
  if (!thread.fileUri) {
    return err('INVALID_THREAD', 'Review thread must have a file URI');
  }

  const threadId = asReviewThreadId(generateId('thread-'));
  const commentId = asReviewCommentId(generateId('comment-'));

  const comment: ReviewComment = {
    id: commentId,
    threadId,
    authorId: thread.initialComment.authorId,
    body: thread.initialComment.body,
    timestamp: now(),
    suggestion: thread.initialComment.suggestion,
    reactions: {},
  };

  const newThread: ReviewThread = {
    id: threadId,
    fileUri: thread.fileUri,
    range: thread.range,
    comments: [comment],
    status: 'open',
    createdAt: now(),
  };

  return ok(
    {
      ...state,
      base: { ...state.base, threads: [...state.base.threads, newThread] },
    },
    'COMMENT_ADDED',
    `Review thread added for ${thread.fileUri}`
  );
}

/**
 * Adds a reply to an existing review thread.
 * @param state - Current review state.
 * @param threadId - Thread to reply to.
 * @param reply - Reply comment fields.
 * @returns EditorResult wrapping updated FullReviewState.
 */
export function replyToComment(
  state: FullReviewState,
  threadId: ReviewThreadId,
  reply: Omit<ReviewComment, 'id' | 'threadId' | 'timestamp'>
): EditorResult<FullReviewState> {
  const thread = state.base.threads.find((t) => t.id === threadId);
  if (!thread) {
    return err('THREAD_NOT_FOUND', `Review thread "${threadId}" not found`);
  }
  if (thread.status === 'resolved') {
    return err('THREAD_RESOLVED', `Cannot reply to resolved thread "${threadId}"`);
  }

  const comment: ReviewComment = {
    id: asReviewCommentId(generateId('comment-')),
    threadId,
    authorId: reply.authorId,
    body: reply.body,
    timestamp: now(),
    suggestion: reply.suggestion,
    reactions: reply.reactions ?? {},
  };

  const updatedThread: ReviewThread = {
    ...thread,
    comments: [...thread.comments, comment],
  };

  const updatedThreads = state.base.threads.map((t) =>
    t.id === threadId ? updatedThread : t
  );

  return ok(
    { ...state, base: { ...state.base, threads: updatedThreads } },
    'REPLY_ADDED',
    `Reply added to thread "${threadId}"`
  );
}

/**
 * Marks a review thread as resolved.
 * @param state - Current review state.
 * @param threadId - Thread to resolve.
 * @returns EditorResult wrapping updated FullReviewState.
 */
export function resolveThread(
  state: FullReviewState,
  threadId: ReviewThreadId
): EditorResult<FullReviewState> {
  const thread = state.base.threads.find((t) => t.id === threadId);
  if (!thread) {
    return err('THREAD_NOT_FOUND', `Review thread "${threadId}" not found`);
  }

  const updatedThread: ReviewThread = { ...thread, status: 'resolved' };
  const updatedThreads = state.base.threads.map((t) =>
    t.id === threadId ? updatedThread : t
  );

  return ok(
    { ...state, base: { ...state.base, threads: updatedThreads } },
    'THREAD_RESOLVED',
    `Thread "${threadId}" resolved`
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// §5 SUGGESTIONS — addSuggestion, applySuggestion
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Adds a code suggestion to a review thread.
 * @param state - Current review state.
 * @param range - Code range the suggestion applies to.
 * @param replacement - Replacement text to suggest.
 * @returns EditorResult wrapping updated FullReviewState.
 */
export function addSuggestion(
  state: FullReviewState,
  range: Range,
  replacement: string
): EditorResult<FullReviewState> {
  if (!replacement && replacement !== '') {
    return err('INVALID_SUGGESTION', 'Suggestion replacement cannot be undefined');
  }

  // Attach to the most recent open thread, or create an implicit suggestion
  const openThread = [...state.base.threads].reverse().find((t) => t.status === 'open');
  const threadId: ReviewThreadId = openThread?.id ?? asReviewThreadId('implicit');

  const suggestion: ReviewSuggestion = {
    id: generateId('suggestion-'),
    threadId,
    range,
    replacement,
    isApplied: false,
    createdAt: now(),
  };

  return ok(
    { ...state, suggestions: [...state.suggestions, suggestion] },
    'SUGGESTION_ADDED',
    `Code suggestion added for range ${range.start.line}:${range.start.column}–${range.end.line}:${range.end.column}`
  );
}

/**
 * Marks a suggestion as applied.
 * @param state - Current review state.
 * @param suggestionId - ID of suggestion to apply.
 * @returns EditorResult wrapping updated FullReviewState.
 */
export function applySuggestion(
  state: FullReviewState,
  suggestionId: string
): EditorResult<FullReviewState> {
  const suggestion = state.suggestions.find((s) => s.id === suggestionId);
  if (!suggestion) {
    return err('SUGGESTION_NOT_FOUND', `Suggestion "${suggestionId}" not found`);
  }
  if (suggestion.isApplied) {
    return err('ALREADY_APPLIED', `Suggestion "${suggestionId}" is already applied`);
  }

  const updated = state.suggestions.map((s) =>
    s.id === suggestionId ? { ...s, isApplied: true } : s
  );

  return ok(
    { ...state, suggestions: updated },
    'SUGGESTION_APPLIED',
    `Suggestion "${suggestionId}" applied`
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// §6 APPROVALS — setApprovalState, submitReview
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Sets or updates a reviewer's approval state.
 * @param state - Current review state.
 * @param userId - Reviewer user ID.
 * @param approval - Approval state to set.
 * @returns EditorResult wrapping updated FullReviewState.
 */
export function setApprovalState(
  state: FullReviewState,
  userId: string,
  approval: ApprovalState
): EditorResult<FullReviewState> {
  const existing = state.approvals.find((a) => a.userId === userId);
  const entry: ReviewApproval = {
    userId,
    state: approval,
    submittedAt: now(),
    comment: existing?.comment,
  };

  const updatedApprovals = existing
    ? state.approvals.map((a) => (a.userId === userId ? entry : a))
    : [...state.approvals, entry];

  return ok(
    { ...state, approvals: updatedApprovals },
    'APPROVAL_SET',
    `User "${userId}" set approval to "${approval}"`
  );
}

/**
 * Finalizes and submits the review, transitioning from 'draft' to 'submitted'.
 * @param state - Current review state.
 * @returns EditorResult wrapping updated FullReviewState.
 */
export function submitReview(state: FullReviewState): EditorResult<FullReviewState> {
  if (state.base.status === 'submitted' || state.base.status === 'approved') {
    return err('ALREADY_SUBMITTED', `Review is already in "${state.base.status}" status`);
  }

  const dominantApproval = state.approvals.find((a) => a.state === 'changes-requested');
  const anyApproved = state.approvals.some((a) => a.state === 'approved');

  const newStatus: ReviewState['status'] = dominantApproval
    ? 'changes-requested'
    : anyApproved
    ? 'approved'
    : 'submitted';

  return ok(
    { ...state, base: { ...state.base, status: newStatus } },
    'REVIEW_SUBMITTED',
    `Review submitted with status "${newStatus}"`
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// §7 QUERIES — getReviewSummary, getThreadsForLine
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Returns a summary of the review's activity counts.
 * @param state - Current review state.
 * @returns ReviewSummary with thread, suggestion, and approval counts.
 */
export function getReviewSummary(state: FullReviewState): ReviewSummary {
  const threads = state.base.threads;
  return {
    threadCount: threads.length,
    openThreadCount: threads.filter((t) => t.status === 'open').length,
    resolvedThreadCount: threads.filter((t) => t.status === 'resolved').length,
    suggestionCount: state.suggestions.length,
    appliedSuggestionCount: state.suggestions.filter((s) => s.isApplied).length,
    approvalCount: state.approvals.filter((a) => a.state === 'approved').length,
    changesRequestedCount: state.approvals.filter((a) => a.state === 'changes-requested').length,
  };
}

/**
 * Returns all review threads that touch a specific line number.
 * @param state - Current review state.
 * @param line - Line number to query (0-indexed).
 * @returns Array of ReviewThread where the thread range includes the line.
 */
export function getThreadsForLine(
  state: FullReviewState,
  line: number
): ReviewThread[] {
  return state.base.threads.filter(
    (t) => t.range.start.line <= line && t.range.end.line >= line
  );
}
